import React, { useEffect, useState, useMemo } from "react";
import {
  Card, CardHeader, CardBody, Table, Button, Alert, Label, Input,
} from "reactstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { campaignRecipientsRequest } from "store/campaigns/actions";
import { ApplicationState } from "store";

// Option type for react-select
type OptionType = { value: string; label: string };

// Type for events inside recipient
type RecipientEvent = {
  event_type: string; // e.g., "SENT", "OPENED", "CLICKED"
  created_at: string; // ISO datetime
};

type Recipient = {
  id: string;
  email?: string;
  phone?: string;
  status: string;
  external_data?: Record<string, any>;
  events: RecipientEvent[];
};

const STATUS_OPTIONS: OptionType[] = [
  { value: "", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "SENT", label: "Sent" },
  { value: "FAILED", label: "Failed" },
  { value: "BOUNCED", label: "Bounced" },
];
const YESNO_OPTIONS: OptionType[] = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" }
];

interface Props {
  campaignId: string;
  size?: number;
}

// Helper to check if a given event exists for a recipient
const getEventStatus = (events: RecipientEvent[] | undefined, type: string) => {
  if (!Array.isArray(events)) return false;
  return events.some(e => e.event_type === type);
};

// Optionally get event timestamp if you want to display it
const getEventTime = (events: RecipientEvent[] | undefined, type: string) => {
  if (!Array.isArray(events)) return null;
  const filtered = events.filter(e => e.event_type === type);
  if (filtered.length === 0) return null;
  return filtered[filtered.length - 1].created_at;
};

const CampaignRecipients: React.FC<Props> = ({ campaignId, size = 20 }) => {
  const dispatch = useDispatch<any>();

  // Redux state
  const {
    recipients,
    recipientsPagination: pagination,
    recipLoading: loading,
    recipError: error,
  } = useSelector((s: ApplicationState) => s.campaign);

  // Filter state (local to component)
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sent, setSent] = useState("");
  const [opened, setOpened] = useState("");
  const [clicked, setClicked] = useState("");
  const [page, setPage] = useState(0);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(handler);
  }, [search]);

  // Effect: fetch recipients on mount & when filters/pagination change
  useEffect(() => {
    dispatch(
      campaignRecipientsRequest({
        campaignId,
        page,
        size,
        search: debouncedSearch,
        status,
        sent,
        opened,
        clicked,
      } as any)
    );
  }, [campaignId, page, size, debouncedSearch, status, sent, opened, clicked, dispatch]);

  // Reset filters
  const handleReset = () => {
    setSearch("");
    setStatus("");
    setSent("");
    setOpened("");
    setClicked("");
    setPage(0);
  };

  // Shimmer loading rows
  const shimmerRows = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="placeholder-glow">
          <td><span className="placeholder col-6"></span></td>
          <td><span className="placeholder col-8"></span></td>
          <td><span className="placeholder col-4"></span></td>
          <td><span className="placeholder col-2"></span></td>
          <td><span className="placeholder col-2"></span></td>
          <td><span className="placeholder col-2"></span></td>
        </tr>
      )),
    []
  );

  const renderExternalData = (external_data?: any) => {
    if (!external_data) return null;
    let obj: Record<string, any> | null = null;
    if (typeof external_data === "string") {
      try {
        obj = JSON.parse(external_data);
      } catch {
        return <div className="text-danger small">Invalid external_data</div>;
      }
    } else {
      obj = external_data;
    }
    if (!obj) return null;
    return (
      <div className="small text-muted mt-1">
        {obj.name && <div><b>Name:</b> {obj.name}</div>}
        {obj.email && <div><b>Email:</b> {obj.email}</div>}
        {obj.mobile && <div><b>Mobile:</b> {obj.mobile}</div>}
      </div>
    );
  };

  return (
    <Card className="shadow-sm mb-4">
      <CardHeader className="border-bottom-0">
        <h5 className="fw-semibold mb-0 d-flex align-items-center">
          <i className="ri-user-3-line me-2 text-primary" />
          Recipients
        </h5>
      </CardHeader>
      <CardBody>
        {/* FILTERS */}
        <div className="mb-4 px-3 py-3 rounded-2 border">
          <div className="d-flex justify-content-start align-items-center mb-2 gap-2">
            <h6 className="fw-semibold mb-0">Filters</h6>
            <Button
              color="danger"
              className="btn-label btn-sm"
              disabled={
                !(
                  search ||
                  status ||
                  sent ||
                  opened ||
                  clicked
                )
              }
              onClick={handleReset}
            >
              <i className="ri-refresh-line label-icon align-middle fs-16 me-2"></i>
              Reset
            </Button>
          </div>
          <div className="row g-3">
            {/* Search */}
            <div className="col-md-3">
              <Label className="form-label text-muted fw-medium small mb-1">
                <i className="ri-search-line me-1 text-info" />
                Search
              </Label>
              <Input
                type="text"
                placeholder="Search name, email, phone, or external fields..."
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </div>
            {/* Status */}
            <div className="col-md-2">
              <Label className="form-label text-muted fw-medium small mb-1">
                <i className="ri-shield-line me-1 text-warning" />
                Status
              </Label>
              <Select
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find(opt => opt.value === status)}
                onChange={(opt: any) => {
                  setStatus(opt?.value || "");
                  setPage(0);
                }}
                menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                styles={{
                  menuPortal: (base: any) => ({ ...base, zIndex: 2000 })
                }}
                isClearable
                classNamePrefix="react-select"
              />
            </div>
            {/* Sent */}
            <div className="col-md-2">
              <Label className="form-label text-muted fw-medium small mb-1">
                <i className="ri-send-plane-line me-1 text-success" />
                Sent
              </Label>
              <Select
                options={YESNO_OPTIONS}
                value={YESNO_OPTIONS.find(opt => opt.value === sent)}
                onChange={(opt: any) => {
                  setSent(opt?.value || "");
                  setPage(0);
                }}
                menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                styles={{
                  menuPortal: (base: any) => ({ ...base, zIndex: 2000 })
                }}
                isClearable
                classNamePrefix="react-select"
              />
            </div>
            {/* Opened */}
            <div className="col-md-2">
              <Label className="form-label text-muted fw-medium small mb-1">
                <i className="ri-eye-line me-1 text-primary" />
                Opened
              </Label>
              <Select
                options={YESNO_OPTIONS}
                value={YESNO_OPTIONS.find(opt => opt.value === opened)}
                onChange={(opt: any) => {
                  setOpened(opt?.value || "");
                  setPage(0);
                }}
                menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                styles={{
                  menuPortal: (base: any) => ({ ...base, zIndex: 2000 })
                }}
                isClearable
                classNamePrefix="react-select"
              />
            </div>
            {/* Clicked */}
            <div className="col-md-2">
              <Label className="form-label text-muted fw-medium small mb-1">
                <i className="ri-mouse-line me-1 text-danger" />
                Clicked
              </Label>
              <Select
                options={YESNO_OPTIONS}
                value={YESNO_OPTIONS.find(opt => opt.value === clicked)}
                onChange={(opt: any) => {
                  setClicked(opt?.value || "");
                  setPage(0);
                }}
                menuPortalTarget={typeof window !== "undefined" ? document.body : null}
                styles={{
                  menuPortal: (base: any) => ({ ...base, zIndex: 2000 })
                }}
                isClearable
                classNamePrefix="react-select"
              />
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="table-responsive" style={{ maxHeight: 600 }}>
          <Table hover striped className="mb-0 align-middle">
            <thead className="bg-light position-sticky top-0" style={{ zIndex: 2 }}>
              <tr>
                <th>Contact</th>
                <th>Status</th>
                <th>Sent</th>
                <th>Opened</th>
                <th>Clicked</th>
              </tr>
            </thead>
            <tbody>
              {loading ? shimmerRows : error ? (
                <tr>
                  <td colSpan={5}>
                    <Alert color="danger" className="mb-0 text-center">
                      <i className="ri-error-warning-line me-2"></i>
                      {error}
                    </Alert>
                  </td>
                </tr>
              ) : recipients.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="text-center p-4 text-muted">
                      <i className="ri-user-search-line fs-2 mb-2 d-block"></i>
                      <div className="fw-semibold">No recipients found.</div>
                      <div className="small">Try adjusting your filters.</div>
                    </div>
                  </td>
                </tr>
              ) : (
                recipients.map((r: Recipient) => (
                  <tr key={r.id}>
                    <td>
                      {renderExternalData(r.external_data)}
                    </td>
                    <td>{r.status}</td>
                    <td>
                      {getEventStatus(r.events, "SENT") ? (
                        <span title={getEventTime(r.events, "SENT") || undefined}>✔️</span>
                      ) : "—"}
                    </td>
                    <td>
                      {getEventStatus(r.events, "OPEN") ? (
                        <span title={getEventTime(r.events, "OPEN") || undefined}>✔️</span>
                      ) : "—"}
                    </td>
                    <td>
                      {getEventStatus(r.events, "CLICK") ? (
                        <span title={getEventTime(r.events, "CLICK") || undefined}>✔️</span>
                      ) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* PAGINATION */}
        {pagination && pagination.pages > 1 && (
          <div className="p-3 d-flex justify-content-end align-items-center gap-2">
            <Button size="sm" color="light" disabled={pagination.first || loading} onClick={() => setPage(0)}>
              First
            </Button>
            <Button size="sm" color="light" disabled={pagination.first || loading} onClick={() => setPage(p => Math.max(0, p - 1))}>
              Prev
            </Button>
            <span className="mx-2 small">
              Page <b>{pagination.page + 1}</b> of <b>{pagination.pages}</b>
            </span>
            <Button size="sm" color="light" disabled={pagination.last || loading} onClick={() => setPage(p => Math.min(p + 1, pagination.pages - 1))}>
              Next
            </Button>
            <Button size="sm" color="light" disabled={pagination.last || loading} onClick={() => setPage(pagination.pages - 1)}>
              Last
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default CampaignRecipients;
